import { useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError } from '../lib/api'
import { findActions } from '../lib/findActions'
import { currentServiceId, moveBody, placedByAClerk, whyNotMove, type MoveChoice } from '../lib/move'
import { serviceLabel } from '../lib/plan'
import type { RowCustomer } from '../lib/receptionDesk'
import { explain } from '../lib/staffErrors'
import {
  draftsFrom,
  fieldsSave,
  offeredFields,
  refusalFor,
  strandedKeys,
  strandedNote,
  valueProblem,
  type Refusal,
} from '../lib/ticketFields'
import type { CounterView, Service, Ticket, VisitField } from '../lib/types'
import { Button } from '@heroui/react'
import { MoveForm } from '../ui/MoveForm'
import { NoteHistory, type NoteHistoryHandle } from '../ui/NoteHistory'
import { useStaffText } from '../ui/StaffText'
import { FieldRow } from './receptionFields'
import { Confirm, FormModal, StatusChip } from './receptionKit'

const NOTHING_REFUSED: Refusal = { byKey: {}, rest: '' }
const FRESH_MOVE: MoveChoice = { serviceId: '', placement: 'keep', minutes: 10 }

/**
 * One of today's tickets: where it is now, the values it carries (editable in
 * every state), its notes (added while open), and move, put back and cancel.
 */
export function ReceptionTicket({
  ticket,
  services,
  desks,
  declared,
  where,
  tone,
  facts,
  customer,
  canPrint,
  canSupervise,
  onClose,
  onPrint,
  onDone,
  start,
}: {
  ticket: Ticket
  services: Service[]
  /** The branch's desks, offered as a transfer's To. */
  desks: readonly CounterView[]
  /** Null where this person may not read the declarations: no values offered. */
  declared: VisitField[] | null
  where: { main: string; sub?: string }
  /** The status chip's colour. */
  tone?: string
  facts: string
  /** Who the row names; a desk's visit customer is shown here read-only. */
  customer?: RowCustomer
  canPrint: boolean
  canSupervise: boolean
  onClose: () => void
  onPrint: () => void
  /** Something changed on the server: the ticket as it came back, and the toast. */
  onDone: (ticket: Ticket, said: string, close: boolean, notice?: boolean) => void
  /** Opened from a row's Transfer or Cancel: that step is already open. */
  start?: 'move' | 'cancel'
}) {
  const { lang, say } = useStaffText()
  const offered = useMemo(() => (declared ? offeredFields(declared) : []), [declared])
  // Mounted fresh for each ticket (keyed on its id), so what the ticket arrived
  // carrying is the starting point and no other customer's half-typed name is.
  const [drafts, setDrafts] = useState<Record<string, string>>(() => draftsFrom(offered, ticket.fields))
  // What the drawer opened with: a save is judged against this, never the live
  // ticket, so a refresh after Stop texts cannot make the phone box look changed.
  const opened = useRef(ticket.fields)
  // The declarations can arrive after the drawer opened. A box that appears then
  // starts from what the ticket carries, never empty: an empty box here is a
  // value the next Save would delete.
  useEffect(() => {
    setDrafts((current) => {
      const missing = offered.filter((f) => !(f.key in current))
      if (missing.length === 0) return current
      return { ...current, ...draftsFrom(missing, ticket.fields) }
    })
  }, [offered, ticket.fields])
  const notes = useRef<NoteHistoryHandle>(null)
  const [refusal, setRefusal] = useState<Refusal>(NOTHING_REFUSED)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [moving, setMoving] = useState<MoveChoice | null>(() => (start === 'move' ? FRESH_MOVE : null))
  const [moveRefused, setMoveRefused] = useState('')
  const [confirmVoid, setConfirmVoid] = useState(start === 'cancel')

  const number = ticket.displayNumber
  const actions = findActions(ticket, canSupervise)
  const toSave = fieldsSave(offered, opened.current, drafts)
  const dirty = declared !== null && toSave.changed.length > 0
  const stranded = declared ? strandedKeys(declared, ticket.fields) : []

  // What this desk can already see is wrong, in the words the server would use.
  const mine: Record<string, string> = {}
  for (const field of offered) {
    const problem = valueProblem(field, (drafts[field.key] ?? '').trim(), lang)
    if (problem !== '') mine[field.key] = problem
  }
  const anyProblem = Object.keys(mine).length > 0

  // The drawer focuses its first field. On a touch screen that pops the keyboard
  // over the very buttons the greeter opened it for (print, put back, cancel).
  // This effect runs after the drawer's, so it can undo that.
  useEffect(() => {
    if (typeof matchMedia === 'function' && matchMedia('(hover: none)').matches) {
      ;(document.activeElement as HTMLElement | null)?.blur()
    }
  }, [])

  // Leaving with the cancel question unanswered must not look like a cancel.
  const close = () => {
    if (confirmVoid) onDone(ticket, say('reception.notCancelled', { number }), true, true)
    else onClose()
  }

  async function save() {
    setBusy(true)
    setError('')
    let latest = ticket
    try {
      if (!(await (notes.current?.save() ?? Promise.resolve(true)))) return
      if (dirty) {
        try {
          latest = (await api.setTicketFields(ticket.id, toSave.fields, toSave.changed)).ticket
        } catch (err) {
          // refusalFor files a sentence by field key, so it needs the server's own
          // English words: explain would translate them first.
          const message =
            err instanceof ApiError && err.code === 'VALIDATION_FAILED' ? err.message : explain(err, lang, 'common.unreachable')
          setRefusal(refusalFor(message, offered.map((f) => f.key)))
          return
        }
      }
      onDone(latest, say('reception.saved', { number }), true)
    } finally {
      setBusy(false)
    }
  }

  async function act(action: 'recycle' | 'void' | 'move', body: Record<string, unknown>, said: string) {
    setBusy(true)
    setError('')
    try {
      // An unsent note goes first, while the visit is still where it was.
      if (action === 'move' && !(await (notes.current?.save() ?? Promise.resolve(true)))) return
      const { ticket: back } = await api.ticketAction(ticket.id, action, body)
      onDone(back, said, true)
    } catch (err) {
      const message = explain(err, lang, 'common.unreachable')
      if (action === 'move') setMoveRefused(message)
      else setError(message)
      setConfirmVoid(false)
    } finally {
      setBusy(false)
    }
  }

  function move(choice: MoveChoice) {
    const refused = whyNotMove(currentServiceId(ticket), choice, placedByAClerk(ticket), lang)
    if (refused) {
      setMoveRefused(refused)
      return
    }
    void act('move', moveBody(choice), say('reception.moved', { number }))
  }

  const planned = ticket.plannedServices ?? []

  return (
    <FormModal
      title={say('reception.ticketTitle', { number })}
      sub={facts}
      onClose={close}
      onSubmit={() => void save()}
      submitLabel={say('reception.saveTicket')}
      valid={dirty && !anyProblem}
      busy={busy}
      hideSubmit={!dirty}
      closeLabel={say('ui.close')}
      extra={
        canPrint ? (
          <Button variant="secondary" size="sm" onPress={onPrint}>
            {say('reception.printAgain')}
          </Button>
        ) : undefined
      }
    >
      <div className="reception-status" data-state={ticket.state}>
        <StatusChip tone={tone}>{where.main}</StatusChip>
        {where.sub && <span>{where.sub}</span>}
        {planned.length > 0 && (
          <span>{say('reception.then', { list: planned.map((id) => serviceLabel(services, id, lang)).join(', ') })}</span>
        )}
      </div>

      {customer?.from === 'visit' && (
        <dl className="reception-customer">
          <dt>{say('reception.col.customer')}</dt>
          <dd>{[customer.name, customer.phone].filter(Boolean).join(' · ')}</dd>
        </dl>
      )}

      {offered.length > 0 && (
        <div className="reception-drawer__fields">
          {offered.map((field) => (
            <FieldRow
              key={field.key}
              field={field}
              value={drafts[field.key] ?? ''}
              disabled={busy}
              problem={mine[field.key] ?? refusal.byKey[field.key] ?? ''}
              onChange={(value) => {
                setDrafts((d) => ({ ...d, [field.key]: value }))
                setRefusal(NOTHING_REFUSED)
              }}
            />
          ))}
        </div>
      )}
      {stranded.length > 0 && <p className="muted reception-small">{strandedNote(stranded, lang)}</p>}
      {refusal.rest !== '' && <p className="reception-error">{refusal.rest}</p>}

      <NoteHistory ticket={ticket} handle={notes} edits={false} />

      {error && <p className="reception-error">{error}</p>}

      {actions.includes('move') &&
        (moving ? (
          <MoveForm
            ticket={ticket}
            services={services}
            desks={desks}
            choice={moving}
            refused={moveRefused}
            onChange={(next) => {
              setMoving(next)
              setMoveRefused('')
            }}
            onCancel={() => {
              setMoving(null)
              setMoveRefused('')
            }}
            onMove={() => move(moving)}
          />
        ) : null)}

      {(actions.length > 0 || confirmVoid) && !moving && (
        <div className="reception-leave">
          {confirmVoid ? (
            <Confirm
              question={say('reception.cancelAsk', { number })}
              yes={say('reception.yesCancel', { number })}
              no={say('reception.keepIt')}
              busy={busy}
              onYes={() => void act('void', { reason: 'withdrawn at reception' }, say('reception.cancelled', { number }))}
              onNo={() => setConfirmVoid(false)}
            />
          ) : (
            <>
              {actions.includes('putBack') && (
                <Button variant="secondary" size="sm" isDisabled={busy} onPress={() => void act('recycle', {}, say('reception.backInLine', { number }))}>
                  {say('reception.putBack')}
                </Button>
              )}
              {actions.includes('move') && (
                <Button variant="secondary" size="sm" onPress={() => setMoving(FRESH_MOVE)}>
                  {say('reception.move')}
                </Button>
              )}
              <span className="reception-grow" />
              {actions.includes('remove') && (
                <Button variant="ghost" size="sm" className="rx-danger" onPress={() => setConfirmVoid(true)}>
                  {say('reception.cancelTicket')}
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </FormModal>
  )
}
